import { AsteriskHand, Icon, Tape } from './ui.jsx';

/* ---------- Status dot ---------- */
export const StatusDot = ({ status }) => {
  const lbl = { live: 'En cours', soon: 'À venir', past: 'Terminé', draft: 'Brouillon' };
  const colors = { live: 'var(--green)', soon: 'var(--yellow)', past: 'var(--paper-3)', draft: 'var(--purple)' };

  return (
    <span className="stamp" style={{ background: colors[status], color: 'var(--ink)' }}>
      {lbl[status]}
    </span>
  );
};

export const ParcoursCard = ({ p, large, tapeColor }) => {
  const D = window.PA_DATA;
  const tapes = ['yellow', 'pink', 'orange', 'purple'];
  const tc = tapeColor || tapes[Math.abs(p.id.charCodeAt(0)) % 4];
  const dateLabel = p.dates || [p.dateStart, p.dateEnd].filter(Boolean).join(' · ');
  // Sans date, ni le badge calendrier ni le tampon de statut n'ont quoi que ce soit à annoncer.
  const hasDate = Boolean(dateLabel);
  const metaLine = [dateLabel, p.region].filter(Boolean).join(' · ');

  return (
    <a
      href={`/parcours/${p.slug}`}
      className="card card-hover stack relative"
      style={{
        minHeight: large ? 540 : 460,
        gridColumn: large ? 'span 2' : 'auto',
        borderRadius: 16,
        border: '1.5px solid var(--ink)',
        background: 'var(--paper-warm)',
        overflow: 'visible'
      }}
    >
      <Tape color={tc} style={{ top: -12, left: 24, width: 120, height: 24 }} />
      <div className="ph" style={{ height: large ? 240 : 170, flex: '0 0 auto', position: 'relative', background: 'var(--paper-3)', overflow: 'hidden', borderRadius: '14px 14px 0 0' }}>
        {/* Top-left: date badge + status stamp stacked so they never overlap */}
        {hasDate && (
          <div className="stack gap-2" style={{ position: 'absolute', top: 16, left: 16, zIndex: 2, alignItems: 'flex-start' }}>
            <div
              className="row"
              style={{
                background: 'var(--yellow)',
                color: 'var(--ink)',
                border: '1.5px solid var(--ink)',
                borderRadius: 6,
                padding: '8px 10px',
                transform: 'rotate(-2deg)',
                boxShadow: 'var(--shadow)',
                gap: 6
              }}
            >
              <Icon name="calendar" size={12} />
              <span className="mono" style={{ fontSize: 10, letterSpacing: '0.08em' }}>{dateLabel}</span>
            </div>
            <StatusDot status={p.status} />
          </div>
        )}
        <img
          src={p.image || D.photo(p.id, large ? 1400 : 900, large ? 900 : 700)}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
        {/* Top-right: artist count — hidden when there are none */}
        {p.artistCount > 0 && (
          <span className="stamp" style={{ position: 'absolute', top: 16, right: 16, zIndex: 2, background: 'var(--ink)', color: 'var(--paper)' }}>
            {p.artistCount} artiste{p.artistCount > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="stack gap-3" style={{ padding: '22px 24px 24px', flex: 1, minHeight: 0 }}>
        {metaLine && <div className="mono muted">{metaLine}</div>}
        <h3 style={{ fontSize: large ? 38 : 30, lineHeight: 1.02 }}>{p.title}</h3>
        <p className={large ? 'muted clamp-4' : 'muted clamp-3'} style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>
          {large ? p.description : p.summary}
        </p>
        <div className="row between" style={{ marginTop: 'auto', paddingTop: 6 }}>
          <div className="row gap-4 mono muted">
            {p.venueCount > 0 && (
              <span><Icon name="pin" size={12} /> {p.venueCount} lieu{p.venueCount > 1 ? 'x' : ''}</span>
            )}
            {p.days > 0 && (
              <span><Icon name="calendar" size={12} /> {p.days} jour{p.days > 1 ? 's' : ''}</span>
            )}
          </div>
          <span className="btn-pill"><Icon name="arrow-up-right" size={16} /></span>
        </div>
      </div>
    </a>
  );
};

/* ---------- ArtistCard ---------- */
export const ArtistCard = ({ a, size = 'md', onClick }) => {
  const heights = { sm: 240, md: 340, lg: 460 };
  const tapes = ['yellow', 'pink', 'orange', 'purple'];
  const tc = tapes[Math.abs(a.id.charCodeAt(0)) % 4];
  const accents = ['var(--pink)', 'var(--yellow)', 'var(--orange)', 'var(--purple)', 'var(--green)', 'var(--blue)'];
  const accent = accents[Math.abs(a.id.charCodeAt(1) || 0) % accents.length];
  const hasPhoto = !!a.photo;
  const initials = a.name.split(/[\s\-]+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const handle = (e) => {
    e.preventDefault();
    if (onClick) onClick(a);
    else window.dispatchEvent(new CustomEvent('open-artist', { detail: a }));
  };

  return (
    <a href="#" onClick={handle} className="stack gap-3 cursor relative" style={{ textDecoration: 'none' }}>
      <Tape color={tc} style={{ top: -8, left: 18, width: 80, height: 18, zIndex: 6 }} />
      <div className="relative" style={{ height: heights[size], overflow: 'hidden', background: hasPhoto ? 'var(--ink)' : accent, borderRadius: 8, border: '1.5px solid var(--ink)' }}>
        {hasPhoto && (
          <img
            src={a.photo}
            alt={a.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'grayscale(100%) contrast(1.05) brightness(0.95)' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}

        {!hasPhoto && (
          <>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'var(--font-display)',
                fontWeight: 900,
                fontSize: size === 'lg' ? 120 : 90,
                color: 'var(--ink)',
                opacity: 0.15
              }}
            >
              {initials}
            </div>
            <AsteriskHand color="var(--ink)" size={size === 'lg' ? 56 : 44} style={{ position: 'absolute', top: 16, right: 16, opacity: 0.5 }} />
          </>
        )}

        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '24px 22px 16px',
            background: hasPhoto ? 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 90%)' : 'transparent',
            color: hasPhoto ? 'var(--paper)' : 'var(--ink)'
          }}
        >
          <div className="display" style={{ fontSize: size === 'lg' ? 40 : 30, lineHeight: 0.9, letterSpacing: '-0.025em' }}>
            {a.name.split(' ').map((w, i) => (
              <div key={i}>{w}</div>
            ))}
          </div>
          {!hasPhoto && a.discipline && (
            <div className="mono" style={{ marginTop: 8, fontSize: 10, opacity: 0.7 }}>
              {a.discipline.toUpperCase().slice(0, 40)}
            </div>
          )}
        </div>

        <button
          className="btn-pill"
          style={{
            position: 'absolute',
            right: 16,
            bottom: 16,
            width: 36,
            height: 36,
            background: hasPhoto ? 'var(--paper)' : 'var(--ink)',
            color: hasPhoto ? 'var(--ink)' : 'var(--paper)'
          }}
        >
          <Icon name="plus" size={16} />
        </button>
      </div>
    </a>
  );
};

/* ---------- Artist Modal ---------- */
export const ArtistModal = ({ artist, onClose }) => {
  const D = window.PA_DATA;

  useEffect(() => {
    if (!artist) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [artist]);

  if (!artist) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(15,15,15,0.78)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: 'fadeUp .25s'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          maxWidth: 880,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: 0,
          borderRadius: 16,
          border: '1.5px solid var(--ink)',
          background: 'var(--paper-warm)',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr)'
        }}
      >
        <div className="relative" style={{ background: artist.photo ? 'var(--ink)' : 'var(--pink)', minHeight: 400 }}>
          {artist.photo ? (
            <img
              src={artist.photo}
              alt={artist.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(100%) contrast(1.05)' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 32,
                color: 'var(--ink)'
              }}
            >
              <div className="display" style={{ fontSize: 96, lineHeight: 0.85, letterSpacing: '-0.03em' }}>
                {artist.name.split(/[\s\-]+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
              </div>
            </div>
          )}
        </div>

        <div className="stack gap-5" style={{ padding: 36, position: 'relative' }}>
          <button onClick={onClose} className="btn-pill" style={{ position: 'absolute', top: 16, right: 16, width: 38, height: 38 }}>
            <Icon name="close" size={16} />
          </button>
          <span className="stamp" style={{ background: 'var(--pink)', color: 'var(--ink)' }}>Artiste</span>
          <h2 style={{ fontSize: 54, lineHeight: 0.9 }}>{artist.name}</h2>
          <p style={{ fontSize: 15.5, lineHeight: 1.6, color: 'var(--ink-soft)', margin: 0 }}>{artist.bio}</p>
          <div className="divider" />
          <div className="stack gap-2">
            <span className="mono muted">Participe à</span>
            <div className="stack gap-2">
              {(artist.parcours || []).map((pid) => {
                const p = D.parcours.find((x) => x.id === pid);
                if (!p) return null;

                return (
                  <a
                    key={pid}
                    href={`/parcours/${p.slug}`}
                    onClick={onClose}
                    className="row between"
                    style={{ padding: '12px 16px', borderRadius: 8, background: 'var(--paper-2)', border: '1px solid var(--line)' }}
                  >
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{p.title}</span>
                    <span className="mono muted">{p.dates}</span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ArtistModalHost = () => {
  const [artist, setArtist] = useState(null);

  useEffect(() => {
    const onOpen = (e) => setArtist(e.detail);
    window.addEventListener('open-artist', onOpen);
    return () => window.removeEventListener('open-artist', onOpen);
  }, []);

  return <ArtistModal artist={artist} onClose={() => setArtist(null)} />;
};

/* ---------- Small artist row ---------- */
export const ArtistRow = ({ a }) => {
  const hasPhoto = !!a.photo;
  const initials = a.name.split(/[\s\-]+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const open = (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('open-artist', { detail: a }));
  };

  return (
    <a href="#" onClick={open} className="row gap-3" style={{ textDecoration: 'none' }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          overflow: 'hidden',
          flexShrink: 0,
          background: hasPhoto ? 'var(--paper-3)' : 'var(--ink)',
          color: 'var(--paper)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-display)',
          fontSize: 14,
          fontWeight: 900
        }}
      >
        {hasPhoto ? (
          <img
            src={a.photo}
            alt={a.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(100%)' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : initials}
      </div>
      <div className="stack" style={{ lineHeight: 1.2 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{a.name}</span>
      </div>
    </a>
  );
};

/* ---------- VenueCard ---------- */
export const VenueCard = ({ v }) => {
  const hasPhoto = !!v.image;

  const openOnMap = () => {
    window.dispatchEvent(new CustomEvent('focus-venue', { detail: v }));
  };

  const typeColors = {
    Atelier: 'var(--pink)',
    Galerie: 'var(--purple)',
    'Centre culturel': 'var(--green)',
    Monument: 'var(--blue)',
    'Lieu rural': 'var(--orange)',
    'Lieu collectif': 'var(--yellow)',
    Salle: 'var(--purple)',
    'Gîte': 'var(--orange)'
  };

  const accent = typeColors[v.type] || 'var(--pink)';

  return (
    <div className="card stack relative" style={{ padding: 0, overflow: 'visible', borderRadius: 12, border: '1.5px solid var(--ink)' }}>
      <Tape color="yellow" style={{ top: -8, right: 18, width: 60, height: 16 }} />
      <div className="ph" style={{ height: 180, position: 'relative', background: hasPhoto ? 'var(--paper-3)' : accent, overflow: 'hidden', borderRadius: '10px 10px 0 0' }}>
        {hasPhoto ? (
          <img
            src={v.image}
            alt={v.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--ink)',
                opacity: 0.85
              }}
            >
              <Icon name="pin" size={56} stroke={1.8} />
            </div>
            {v.number !== undefined && (
              <div
                style={{
                  position: 'absolute',
                  top: 14,
                  left: 14,
                  fontFamily: 'var(--font-display)',
                  fontSize: 42,
                  fontWeight: 900,
                  color: 'var(--ink)',
                  lineHeight: 1,
                  opacity: 0.85
                }}
              >
                {String(v.number).padStart(2, '0')}
              </div>
            )}
          </>
        )}
      </div>
      <div className="stack gap-2" style={{ padding: 18 }}>
        <div className="mono muted">{v.type} · {v.commune}</div>
        <div className="display" style={{ fontSize: 24, lineHeight: 0.95 }}>{v.name}</div>
        {v.address && <div className="muted" style={{ fontSize: 12, lineHeight: 1.4 }}>{v.address}</div>}
        <div className="row gap-2" style={{ marginTop: 6 }}>
          <button onClick={openOnMap} className="btn btn-light btn-sm">
            <Icon name="map" size={13} /> Sur la carte
          </button>
          <button onClick={openOnMap} className="btn-pill" style={{ width: 32, height: 32 }}>
            <Icon name="arrow-up-right" size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};
